/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM, IIconPackageURLMapping, useTranslate } from "@adapptio/lib-components";
import React, { FC, useCallback, useState, useRef } from "react";
import { useEscKey } from "../../hooks/useEscKey";
import { FC_ANCHOR, FC_DIRECTION } from "../FloatingContainer";
import { Icon, ICON_SIZE } from "../Icon";
import { IconList } from "../IconList";
import { IIconListPack } from "../IconList/IconListTypes";
import {
	FieldFloatingPanel,
	FIELD_FLOATING_PANEL_SCHEMA,
	FIELD_FLOATING_PANEL_STYLE
} from "./FieldFloatingPanel";
import { IFieldValidationError } from "./FieldTypes";
import { FieldWrapper } from "./FieldWrapper";
import { FieldValidationErrors } from "./FieldValidationErrors";
import { Input } from "../Input";
import { InputAdornment } from "../InputAdornment";
import { ColorPickerTile } from "../ColorPicker";
import { InputButton } from "../InputButton";
import { useClipboard } from "../../hooks/useClipboard";

export interface IFieldIconSelectorProps {
	id?: string;
	value: string;
	iconPacks: IIconListPack[];
	urlMapping?: IIconPackageURLMapping;
	placeholder?: string;
	readOnly?: boolean;
	validationErrors?: IFieldValidationError[];
	className?: string;
	grow?: boolean;
	listAnchor?: FC_ANCHOR;
	listDirection?: FC_DIRECTION;
	listOffset?: number;
	onChange?: (value: string) => void;
}

export const FieldIconSelector: FC<IFieldIconSelectorProps> = ({
	id,
	value,
	iconPacks,
	urlMapping,
	placeholder,
	readOnly,
	validationErrors,
	className,
	grow,
	listAnchor,
	listDirection,
	listOffset,
	onChange
}) => {
	const t = useTranslate();

	const { wasCopiedRecently, copyToClipboard } = useClipboard();

	const [ isListOpened, setIsListOpened ] = useState(false);
	const elRef = useRef<HTMLButtonElement>();

	const toggleIconList = useCallback<React.MouseEventHandler>(() => {
		event.preventDefault();
		setIsListOpened((x) => !x);
	}, []);

	const closeIconList = useCallback(() => {
		setIsListOpened(false);
	}, []);

	const handleSelect = useCallback(
		(packName: string, iconName: string) => {
			onChange(iconName);
			setIsListOpened(false);
		},
		[ onChange ]
	);

	const copyValueToClipboard = useCallback(() => {
		copyToClipboard(value);
	}, [ value ]);

	useEscKey(() => setIsListOpened(false), isListOpened);

	const [ selectedPackName ] = (value || "").split("/", 2);

	const baseClass = BEM("edt-field", "selector", {
		"is-empty": !value,
		"is-opened": isListOpened,
		readonly: readOnly
	});

	baseClass.add("edt-field-control");

	return (
		<FieldWrapper type="icon-selector" className={className} grow={grow} isFocused={isListOpened}>
			<button
				className={baseClass.toClassName()}
				onClick={toggleIconList}
				disabled={readOnly}
				ref={elRef}
			>
				<span className={baseClass.element("selector-label").toClassName()}>
					{value || t("editor", placeholder)}
				</span>
				<FieldValidationErrors errors={validationErrors} />
				<Icon name="mdi/folder-open" size={ICON_SIZE.CUSTOM} />
			</button>
			<button
				className={baseClass.element("adornment-btn").toClassName()}
				onClick={copyValueToClipboard}
				disabled={readOnly || !value}
				tabIndex={-1}
				title={"Copy value"}
			>
				<Icon name={wasCopiedRecently ? "mdi/check" : "mdi/content-copy"} size={ICON_SIZE.XSMALL} />
			</button>

			<FieldFloatingPanel
				for={elRef}
				isOpened={isListOpened}
				anchor={listAnchor || FC_ANCHOR.RIGHT_BOTTOM}
				direction={listDirection || FC_DIRECTION.HR}
				style={FIELD_FLOATING_PANEL_STYLE.STICKY}
				schema={FIELD_FLOATING_PANEL_SCHEMA.PRIMARY}
				offset={listOffset || 0}
				className="edt-field--type-icon-selector__panel"
				onClose={closeIconList}
			>
				<IconList
					iconPacks={iconPacks}
					urlMapping={urlMapping}
					iconPreviewSize={ICON_SIZE.MEDIUM}
					selectedPackName={selectedPackName}
					selectedIconName={value}
					onSelect={handleSelect}
				/>
			</FieldFloatingPanel>
		</FieldWrapper>
	);
};
